




























import {
  computed,
  defineComponent,
  ref,
  watch,
  PropType,
  onMounted,
  nextTick
} from "@vue/composition-api";
import { UserItem } from "@/components/Pages/Scout/Messages/CommentListContainer/UserHeader/types";
import UserHeader from "@/components/Pages/Scout/Messages/CommentListContainer/UserHeader/index.vue";
import { CommentListItem } from "@/components/Pages/Scout/Messages/CommentListContainer/CommentList/types";
import CommentList from "@/components/Pages/Scout/Messages/CommentListContainer/CommentList/index.vue";
import { getThreadComments, getThreadUser } from "@/grpc-web/scout/message";
import { ThreadStatus } from "@/pb/scout/message/v1/message_pb";
import {
  convertCommentsToCommentListItems,
  convertThreadUserHeaderToUserItem,
  findLastUserCommentId,
  getLastMessageItem,
  getLastPartnerCommentItem
} from "./methods";
import { readComment } from "@/grpc-web/scout/message";
import LoadingContainer from "@/components/Pages/Scout/elements/LoadingContainer/index.vue";

export default defineComponent({
  name: "CommentListContainer",
  components: { LoadingContainer, UserHeader, CommentList },
  emits: [
    "delete",
    "open-detail",
    "thread-comment-not-found",
    "update-thread",
    "update-last-partner-comment",
    "read-thread",
    "mouseover",
    "mouseleave"
  ],
  props: {
    messageThreadId: {
      type: Number as PropType<number | undefined>,
      default: undefined
    },
    width: {
      type: String,
      default: "100%"
    },
    updatedAt: {
      type: Number as PropType<number | undefined>,
      default: undefined
    },
    userImageUrl: {
      type: String,
      default: ""
    }
  },
  setup(props, { emit }) {
    const user = ref<UserItem | undefined>(undefined);
    const commentListItems = ref<CommentListItem[]>([]);
    const page = 1;
    // 2023/06/08現在最大で70件までしかないので、100件で固定
    // [2023/08/15追記] 無限ローディングに対応しようとしたが、実装が複雑になりバグが発生したため、一旦無限ローディングは諦めた
    //  c.f. https://github.com/OneCareerJP/cloud/pull/2725
    const per = 100;
    const threadStatus = ref<ThreadStatus>(
      ThreadStatus.THREAD_STATUS_UNSPECIFIED
    );

    const commentsContainer = ref<HTMLElement | null>(null);

    watch(
      () => {
        return props.messageThreadId;
      },
      async newValue => {
        if (newValue) {
          await Promise.all([loadComments(), loadUser()]);
        }
      }
    );

    watch(commentListItems, () => {
      nextTick(() => {
        if (commentsContainer.value) {
          commentsContainer.value.scrollTop =
            commentsContainer.value.scrollHeight;
        }
      });
    });

    const handleDeletedComment = async (commentId: number) => {
      emit("delete", commentId);
    };

    const isLoading = ref<boolean>(false);
    watch(
      () => props.updatedAt,
      async () => {
        if (props.updatedAt === undefined) {
          return;
        }
        isLoading.value = true;
        await loadComments();
        isLoading.value = false;
      }
    );

    onMounted(async () => {
      await Promise.all([loadComments(), loadUser()]);
      nextTick(() => {
        if (commentsContainer.value) {
          commentsContainer.value.scrollTop =
            commentsContainer.value.scrollHeight;
        }
      });
    });

    const loadComments = async () => {
      if (!props.messageThreadId) {
        return;
      }

      const response = await getThreadComments(
        props.messageThreadId,
        page,
        per
      );

      if (response === undefined) {
        // コメントを削除したあとの再ローディング時にコメントが見つからない場合は、メッセージスレッド自体を削除しているので一覧の更新が必要になる
        emit("thread-comment-not-found");
        return;
      }

      commentListItems.value = convertCommentsToCommentListItems(
        response.getCommentsList()
      );
      threadStatus.value = response.getStatus();

      const lastMessage = getLastMessageItem(
        props.messageThreadId,
        commentListItems.value
      );
      emit("update-thread", lastMessage);

      emit(
        "update-last-partner-comment",
        getLastPartnerCommentItem(commentListItems.value)
      );

      // 既読処理
      const lastUserCommentId = findLastUserCommentId(commentListItems.value);
      if (lastUserCommentId) {
        await readComment(lastUserCommentId);
        // 既読処理が完了した時だけ、未読バッジを消去したいので、API後に実行させる
        emit("read-thread", props.messageThreadId);
      }
    };

    const loadUser = async () => {
      if (!props.messageThreadId) {
        return;
      }

      try {
        const threadUser = await getThreadUser(props.messageThreadId);
        if (!threadUser) {
          return;
        }

        user.value = convertThreadUserHeaderToUserItem(threadUser);
      } catch (error) {
        // throw error にするとstorybookでエラーを出てしまうため、returnにしている
        return;
      }
    };

    const handleClickedOpenDetail = (userId: number) => {
      emit("open-detail", userId);
    };

    const handleMouseOverCommentsContainer = () => {
      emit("mouseover");
    };

    const handleMouseLeaveCommentsContainer = () => {
      emit("mouseleave");
    };

    const widthStyle = computed(() => {
      return {
        width: props.width
      };
    });

    return {
      user,
      commentListItems,
      handleClickedOpenDetail,
      widthStyle,
      commentsContainer,
      handleDeletedComment,
      handleMouseOverCommentsContainer,
      handleMouseLeaveCommentsContainer,
      isLoading,
      threadStatus
    };
  }
});
